import {
  LinkFirebaseResultSchema,
  LinkSessionInfoSchema,
  TokenPair,
  TokenPairSchema
} from "@garther/interfaces";

import { type ApiClientConfig, ApiError, BaseApiClient } from "./base-client";
import { LS_ACCESS_TOKEN_KEY, LS_REFRESH_TOKEN_KEY } from "./constants";
import { isExpired } from "./utils";

export class AuthApiClient extends BaseApiClient {
  private _refreshPromise: Promise<TokenPair> | null = null;
  private _onAuthExpired?: () => void;

  setOnAuthExpired(fn: () => void): void {
    this._onAuthExpired = fn;
  }

  constructor(config: Omit<ApiClientConfig, "getToken" | "baseEndpoint" | "requireAuth">) {
    super({
      ...config,
      baseEndpoint: "api/v1/auth",
      requireAuth: false,
      getToken: async () => {
        throw new Error("Not implemented");
      }
    });
  }

  async exchangeToken(provider: "firebase" | "zalo", token: string): Promise<void> {
    console.debug("[DEBUG] AuthApiClient.exchangeToken");
    const pair = await this.post(provider, TokenPairSchema, {
      accessToken: token
    });
    console.debug("[DEBUG] AuthApiClient.exchangeToken", pair);
    localStorage.setItem(LS_ACCESS_TOKEN_KEY, pair.accessToken);
    localStorage.setItem(LS_REFRESH_TOKEN_KEY, pair.refreshToken);
  }

  refreshTokens = async (): Promise<TokenPair> => {
    if (this._refreshPromise) return this._refreshPromise;

    const refreshToken = localStorage.getItem(LS_REFRESH_TOKEN_KEY);
    if (!refreshToken) throw new ApiError("No refresh token", 401);

    this._refreshPromise = this.post("refresh", TokenPairSchema, {
      refreshToken
    })
      .then((pair) => {
        localStorage.setItem(LS_ACCESS_TOKEN_KEY, pair.accessToken);
        localStorage.setItem(LS_REFRESH_TOKEN_KEY, pair.refreshToken);
        return pair;
      })
      .finally(() => {
        this._refreshPromise = null;
      });

    return this._refreshPromise;
  };

  clearTokens(): void {
    localStorage.removeItem(LS_ACCESS_TOKEN_KEY);
    localStorage.removeItem(LS_REFRESH_TOKEN_KEY);
  }

  async getLinkSession(token: string) {
    return this.get(`link/session?token=${encodeURIComponent(token)}`, LinkSessionInfoSchema);
  }

  async linkWithFirebase(linkToken: string, firebaseToken: string) {
    return this.post("link/firebase", LinkFirebaseResultSchema, {
      linkToken,
      firebaseToken
    });
  }

  getToken = async (): Promise<string> => {
    const accessToken = localStorage.getItem(LS_ACCESS_TOKEN_KEY);

    if (!accessToken || isExpired(accessToken)) {
      try {
        const pair = await this.refreshTokens();
        return pair.accessToken;
      } catch (e) {
        this.clearTokens();
        this._onAuthExpired?.();
        throw e;
      }
    }

    return accessToken;
  };
}
